import React, { useState } from 'react';
import { CoursesListPage } from '../../../../../courses-list-page/courses-list-page';
import { ReportCoursesType } from '../../../../../../api/types/reports/education/courses/report-courses.type';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { toast } from 'react-toastify';
import appApi from '../../../../../../api/app-api';
import { UserSummeryAttendance } from '../../../../../../api/types/programs/course/course-details.type';
import { addExternalExamRegistrationsOperation } from '../operations/add-external-exam-registrations-operation';

interface AddExamStudentsEligibilityByCourseProps {
  groupId: number | null;
  examId: number | null;
  onSuccess: () => void;
}

interface SelectedStudent {
  userId: number;
  branchId: number;
  programId: number;
  paymentExempted: boolean;
}

export const AddExamStudentsEligibilityByCourse: React.FC<
  AddExamStudentsEligibilityByCourseProps
> = ({ groupId, examId, onSuccess }) => {
  const [selectedCourse, setSelectedCourse] =
    useState<ReportCoursesType | null>(null);
  const [courseStudents, setCourseStudents] = useState<
    (UserSummeryAttendance & { branchId: number; programId: number })[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState<
    Map<number, SelectedStudent>
  >(new Map());

  const handleCourseClick = async (course: ReportCoursesType) => {
    setSelectedCourse(course);
    setSelectedStudents(new Map()); // Reset selections
    setLoadingStudents(true);

    try {
      // Get programId and branchId from the course
      const programId = course.programs?.[0]?.programId;
      const branchId = course.branchId;
      if (!programId || !branchId) {
        toast.error('לא ניתן למצוא את תוכנית הלימודים או השלוחה של הקורס');
        setLoadingStudents(false);
        return;
      }

      const response = await appApi.getCourseDetails(
        programId,
        course.courseId,
      );
      const students = response.data.body.details.students || [];
      // Add branchId and programId to each student
      const studentsWithIds = students.map((student) => ({
        ...student,
        branchId: branchId,
        programId: programId,
      }));
      setCourseStudents(studentsWithIds);
    } catch (error) {
      console.error('Failed to fetch course students:', error);
      toast.error('שגיאה בטעינת רשימת התלמידים');
      setCourseStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleStudentSelection = (
    student: UserSummeryAttendance & { branchId: number; programId: number },
  ) => {
    const newSelected = new Map(selectedStudents);
    if (newSelected.has(student.id)) {
      newSelected.delete(student.id);
    } else {
      newSelected.set(student.id, {
        userId: student.id,
        branchId: student.branchId,
        programId: student.programId,
        paymentExempted: false,
      });
    }
    setSelectedStudents(newSelected);
  };

  const handlePaymentExemptedChange = (
    studentId: number,
    paymentExempted: boolean,
  ) => {
    const newSelected = new Map(selectedStudents);
    const existing = newSelected.get(studentId);
    if (existing) {
      newSelected.set(studentId, {
        ...existing,
        paymentExempted,
      });
      setSelectedStudents(newSelected);
    }
  };

  const handleSelectAll = () => {
    const allSelected = courseStudents.every((student) =>
      selectedStudents.has(student.id),
    );
    const newSelected = new Map(selectedStudents);

    if (allSelected) {
      // Deselect all
      courseStudents.forEach((student) => {
        newSelected.delete(student.id);
      });
    } else {
      // Select all
      courseStudents.forEach((student) => {
        if (!newSelected.has(student.id)) {
          newSelected.set(student.id, {
            userId: student.id,
            branchId: student.branchId,
            programId: student.programId,
            paymentExempted: false,
          });
        }
      });
    }
    setSelectedStudents(newSelected);
  };

  const isAllSelected =
    courseStudents.length > 0 &&
    courseStudents.every((student) => selectedStudents.has(student.id));

  const handleApprove = async () => {
    if (!groupId || !examId) {
      toast.error('יש לבחור מקבץ ומבחן תחילה');
      return;
    }

    const registrations = Array.from(selectedStudents.values());
    await addExternalExamRegistrationsOperation(
      groupId,
      examId,
      registrations,
      setLoading,
      onSuccess,
    );
  };

  const handleBack = () => {
    setSelectedCourse(null);
    setCourseStudents([]);
    setSelectedStudents(new Map());
  };

  // Show course students table if course is selected
  if (selectedCourse) {
    return (
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <div>
            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
              קורס: {selectedCourse.courseName}
            </div>
            {selectedCourse.programs?.[0] && (
              <div style={{ fontSize: '14px', color: '#666' }}>
                תוכנית: {selectedCourse.programs[0].abstractProgramName} - מחזור{' '}
                {selectedCourse.programs[0].annualSerialProgramNumber} -{' '}
                {selectedCourse.programs[0].programStartYear}
              </div>
            )}
          </div>
          <Button
            label="חזור לבחירת קורס"
            icon="pi pi-arrow-right"
            onClick={handleBack}
            disabled={loading}
          />
        </div>

        <DataTable
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו סטודנטים בקורס</div>
          }
          value={courseStudents}
          dataKey="id"
          loading={loadingStudents}
          tableStyle={{
            direction: 'rtl',
            marginTop: '16px',
          }}
        >
          <Column
            style={{ textAlign: 'right' }}
            header={
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Checkbox
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  disabled={loading || courseStudents.length === 0}
                />
                <span>בחר</span>
              </div>
            }
            body={(
              rowData: UserSummeryAttendance & {
                branchId: number;
                programId: number;
              },
            ) => (
              <Checkbox
                checked={selectedStudents.has(rowData.id)}
                onChange={() => handleStudentSelection(rowData)}
                disabled={loading}
              />
            )}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="שם משתמש"
            body={(
              rowData: UserSummeryAttendance & {
                branchId: number;
                programId: number;
              },
            ) => (
              <div>
                {rowData.firstName} {rowData.lastName}
              </div>
            )}
          />
          <Column
            style={{ textAlign: 'right' }}
            field="nationalId"
            header="ת.ז."
          />
          <Column
            style={{ textAlign: 'right' }}
            header="פטור מתשלום"
            body={(
              rowData: UserSummeryAttendance & {
                branchId: number;
                programId: number;
              },
            ) => (
              <Checkbox
                checked={
                  selectedStudents.get(rowData.id)?.paymentExempted ?? false
                }
                onChange={(e) =>
                  handlePaymentExemptedChange(rowData.id, e.checked ?? false)
                }
                disabled={!selectedStudents.has(rowData.id) || loading}
              />
            )}
          />
        </DataTable>

        {selectedStudents.size > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '20px',
            }}
          >
            <Button
              onClick={handleApprove}
              loading={loading}
              label={`אישור (${selectedStudents.size} סטודנטים)`}
              style={{ height: '30px' }}
            />
          </div>
        )}
      </div>
    );
  }

  // Show courses list
  return (
    <div style={{ height: '600px', overflow: 'auto' }}>
      <CoursesListPage
        allowNavigation={false}
        onCourseClick={handleCourseClick}
      />
    </div>
  );
};
